import './OfferViewGroupList.scss';

import {
  Accordion,
  Button,
  ButtonStyle,
  Card,
  Checkbox,
  Header,
  IconSize,
  Icons,
  MobileSlideIn,
  SearchBar,
} from '@pdcfrontendui/components';
import { Team, TeamShift } from '@pdcplan/shared/api/TeamPlan';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AccordionItemControlled } from '@pdcfrontendui/components/AccordionItem';
import { TeamMap } from '../api/model';
import { currentLanguage } from '../currentLanguage';
import { searchString } from '@pdcfrontendui/utils';
import { useIsDualView } from '@pdcfrontendui/hooks';
import { ExchangeCandidateFilterEnum } from '@pdcplan/shared/enums';
import { ActiveOfferView } from './ShiftViewEnums';
import { useFormatter } from '../hooks/useLang';

const scss = {
  Comp: 'OfferViewList',
  content: 'content',
  groupList: 'group-list',
  groupItemContents: 'group-item-contents',
  searchContainer: 'search-container',
  noGroupsWrapper: 'no-groups-wrapper',
};

type Props = {
  show: boolean;
  shift: TeamShift;
  teams: TeamMap;
  teamId: string;
  possibleCandidateFilters: ExchangeCandidateFilterEnum[];
  defaultExchangeCandidateFilter: ExchangeCandidateFilterEnum;
  expandedAccordionItems: string[];
  selectedGroups: string[];
  onBackClick: () => void;
  rejectOffer: () => void;
  setActiveOfferView: (activeOfferView: ActiveOfferView) => void;
  setExpandedAccordionItems: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedGroups: React.Dispatch<React.SetStateAction<string[]>>;
};

const OfferViewGroupList = ({
  show,
  shift,
  teams,
  teamId,
  expandedAccordionItems,
  selectedGroups,
  onBackClick,
  setActiveOfferView,
  setExpandedAccordionItems,
  setSelectedGroups,
}: Props) => {
  const isMobile = !useIsDualView();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const formatter = useFormatter();

  const title = formatter.weekdayDayMonthYearCap(shift.period.from);

  const teamList: Team[] = useMemo(() => Object.values(teams), [teams]);

  const filteredAndGroupedTeams: Record<string, Team[]> = useMemo(
    () =>
      teamList
        .filter((f) => searchString(searchText, f.label))
        .reduce<Record<string, Team[]>>((group, team) => {
          const { parentId } = team;

          group[parentId] = group[parentId] ?? [];
          group[parentId]?.push(team);
          return group;
        }, {}),
    [teamList, searchText]
  );

  useEffect(() => {
    if (show) {
      setSelectedGroups((prev) =>
        prev.includes(teamId) ? prev : [...prev, teamId]
      );
      setExpandedAccordionItems((prev) => {
        const parentId = teamList.find((team) => team.id === teamId)?.parentId;
        return parentId ? [...prev, parentId] : prev;
      });
    }
  }, [teamId, show]);

  useEffect(() => {
    if (searchText.length === 0) {
      // Expand the accordions with groups selected
      const accordionsWithSelectedGroups = selectedGroups.reduce<string[]>(
        (prev, curr) => {
          const team = teamList.find((team) => team.id === curr);
          if (team) {
            return [...prev, team.parentId];
          }
          return prev;
        },
        []
      );
      setExpandedAccordionItems(accordionsWithSelectedGroups);
    }
  }, [searchText]);

  useEffect(() => {
    if (searchInputRef.current && isSearchOpen) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (!show) {
      setExpandedAccordionItems([]);
      setSearchText('');
      setIsSearchOpen(false);
    }
  }, [show]);

  return (
    <>
      <MobileSlideIn
        isMobile={isMobile}
        className={scss.Comp}
        bottom={50}
        absolute
      >
        {show && (
          <>
            {!isMobile && <Header></Header>}
            <Header centerize>
              <Header.Left>
                <Icons.ChevronLeft
                  size={IconSize.XSmall}
                  onClick={() => {
                    onBackClick();
                  }}
                  ariaLabel={currentLanguage.Back}
                />
              </Header.Left>
              <Header.Title>{title}</Header.Title>
              <Header.Right>
                <Button
                  variant={ButtonStyle.GhostOnDark}
                  onClick={() => {
                    setActiveOfferView(ActiveOfferView.OfferView);
                  }}
                >
                  {currentLanguage.Next}
                </Button>
              </Header.Right>
            </Header>
            {isSearchOpen && (
              <div className={scss.searchContainer}>
                <SearchBar
                  value={searchText}
                  onChange={(value: string) => {
                    setSearchText(value);
                  }}
                  ref={searchInputRef}
                />
                <Button
                  onClick={() => {
                    setSearchText('');
                    setIsSearchOpen(false);
                  }}
                  variant={ButtonStyle.GhostOnDark}
                >
                  {currentLanguage.Cancel}
                </Button>
              </div>
            )}
            <div className={scss.content}>
              <Card className="confirmation-card">
                <div>{currentLanguage.ChooseGroups}</div>
                <div className="confirmation-buttons">
                  <>
                    {!isSearchOpen && (
                      <Icons.Search
                        onClick={() => {
                          setIsSearchOpen(true);
                        }}
                      />
                    )}
                  </>
                </div>
              </Card>
              {Object.keys(filteredAndGroupedTeams).length > 0 ? (
                <div className={scss.groupList}>
                  <Accordion>
                    {Object.entries(filteredAndGroupedTeams)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map((entry) => {
                        const selectedGroupsCount = entry[1].filter((team) =>
                          selectedGroups.includes(team.id)
                        ).length;

                        const counter =
                          selectedGroupsCount > 0
                            ? selectedGroupsCount
                            : undefined;

                        return (
                          <AccordionItemControlled
                            key={entry[0]}
                            title={
                              teamList.find(
                                (team) => team.parentId === entry[0]
                              )?.unitUilabel ?? ''
                            }
                            expanded={
                              searchText.length > 0
                                ? true
                                : expandedAccordionItems.includes(entry[0])
                            }
                            onToggleExpanded={() => {
                              if (expandedAccordionItems.includes(entry[0])) {
                                setExpandedAccordionItems(
                                  expandedAccordionItems.filter(
                                    (item) => item !== entry[0]
                                  )
                                );
                              } else {
                                setExpandedAccordionItems([
                                  ...expandedAccordionItems,
                                  entry[0],
                                ]);
                              }
                            }}
                            counter={counter}
                          >
                            <div className={scss.groupItemContents}>
                              {entry[1]
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((team) => (
                                  <Checkbox
                                    key={team.id}
                                    checked={selectedGroups.includes(team.id)}
                                    label={team.label}
                                    onClick={() => {
                                      if (selectedGroups.includes(team.id)) {
                                        setSelectedGroups(
                                          selectedGroups.filter(
                                            (group) => group !== team.id
                                          )
                                        );
                                      } else {
                                        setSelectedGroups([
                                          ...selectedGroups,
                                          team.id,
                                        ]);
                                      }
                                    }}
                                  />
                                ))}
                            </div>
                          </AccordionItemControlled>
                        );
                      })}
                  </Accordion>
                </div>
              ) : (
                <div className={scss.noGroupsWrapper}>
                  {currentLanguage.NoOffersInSearch}
                </div>
              )}
            </div>
          </>
        )}
      </MobileSlideIn>
    </>
  );
};

export default OfferViewGroupList;
